/**
 * BackoffPolicy — exponential back-off with full jitter.
 *
 * Algorithm (from AWS: "Exponential Backoff And Jitter"):
 *   delay = random(0, min(maxDelay, base * 2^attempt))
 *
 * The counter resets on explicit reset() or when the caller reports success.
 * It also resets on browser "online" events so reconnects are snappy after
 * coming back from network loss.
 */

export interface BackoffOptions {
  /** Initial base delay in ms (default 500). */
  baseMs?: number;
  /** Maximum delay cap in ms (default 15 000). */
  maxMs?: number;
}

export class BackoffPolicy {
  private readonly base: number;
  private readonly max: number;
  private attempt = 0;
  private onlineListener: (() => void) | null = null;

  constructor(opts: BackoffOptions = {}) {
    this.base = opts.baseMs ?? 500;
    this.max = opts.maxMs ?? 15_000;

    if (typeof window !== 'undefined') {
      this.onlineListener = () => this.reset();
      window.addEventListener('online', this.onlineListener);
    }
  }

  /** Returns the next delay and increments the attempt counter. */
  next(): number {
    const cap = Math.min(this.max, this.base * 2 ** this.attempt);
    const delay = Math.random() * cap;
    this.attempt++;
    return delay;
  }

  /** Reset counter (call after a successful connection). */
  reset(): void {
    this.attempt = 0;
  }

  /** Clean up window listeners. */
  destroy(): void {
    if (this.onlineListener && typeof window !== 'undefined') {
      window.removeEventListener('online', this.onlineListener);
      this.onlineListener = null;
    }
  }
}
